import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { useEffect, useRef, useState } from 'react';

export function cn(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(' ');
}

// ------------------------------------------------------------------- кнопки

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  isLoading?: boolean;
  fullWidth?: boolean;
}

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  /*
   * У выключенной кнопки светло-жёлтый фон, и белый текст на нём не читается —
   * белое по жёлтому в проекте запрещено. Поэтому вместе с фоном меняется
   * и цвет текста.
   */
  primary:
    'bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-700 disabled:bg-brand-100 disabled:text-muted',
  secondary: 'bg-white text-ink border border-line hover:bg-slate-50 disabled:text-muted',
  ghost: 'bg-transparent text-brand-600 hover:bg-brand-50',
  danger: 'bg-white text-danger border border-danger/30 hover:bg-red-50',
};

export function Button({
  variant = 'primary',
  isLoading = false,
  fullWidth = false,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps): React.JSX.Element {
  return (
    <button
      // min-h-11 — 44px, минимальная комфортная цель нажатия на телефоне
      className={cn(
        'inline-flex min-h-11 items-center justify-center gap-2 rounded-xl px-4 text-sm font-medium transition-colors',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600',
        'disabled:cursor-not-allowed',
        BUTTON_VARIANTS[variant],
        fullWidth && 'w-full',
        className,
      )}
      disabled={disabled ?? isLoading}
      {...rest}
    >
      {isLoading && <Spinner className="size-4" />}
      {children}
    </button>
  );
}

export function Spinner({ className }: { className?: string }): React.JSX.Element {
  return (
    <svg className={cn('animate-spin', className ?? 'size-5')} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-90"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
      />
    </svg>
  );
}

// -------------------------------------------------------------------- формы

interface FieldProps {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
  required?: boolean;
}

export function Field({ label, hint, error, children, required }: FieldProps): React.JSX.Element {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-ink">
        {label}
        {required && <span className="text-danger"> *</span>}
      </span>
      {children}
      {error ? (
        <span className="mt-1 block text-xs text-danger">{error}</span>
      ) : hint ? (
        <span className="mt-1 block text-xs text-muted">{hint}</span>
      ) : null}
    </label>
  );
}

const CONTROL_CLASS =
  'w-full min-h-11 rounded-xl border border-line bg-white px-3 text-ink placeholder:text-muted/70 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100';

export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>): React.JSX.Element {
  return <input className={cn(CONTROL_CLASS, className)} {...rest} />;
}

export function Select({
  className,
  children,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement>): React.JSX.Element {
  return (
    <select className={cn(CONTROL_CLASS, 'appearance-none pr-8', className)} {...rest}>
      {children}
    </select>
  );
}

export function Textarea({
  className,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement>): React.JSX.Element {
  return <textarea className={cn(CONTROL_CLASS, 'min-h-24 py-2.5', className)} rows={3} {...rest} />;
}

interface RadioGroupProps<T extends string | number> {
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
  name: string;
}

/**
 * Переключатель из нескольких кнопок. В отличие от выпадающего списка
 * показывает все варианты сразу и выбирается одним касанием — на телефоне
 * это заметно быстрее, когда вариантов мало.
 */
export function RadioGroup<T extends string | number>({
  value,
  options,
  onChange,
  name,
}: RadioGroupProps<T>): React.JSX.Element {
  return (
    <div role="radiogroup" aria-label={name} className="flex flex-wrap gap-2">
      {options.map((option) => {
        const isActive = option.value === value;
        return (
          <button
            key={String(option.value)}
            type="button"
            role="radio"
            aria-checked={isActive}
            onClick={() => onChange(option.value)}
            className={cn(
              'min-h-11 min-w-16 flex-1 rounded-xl border px-3 text-sm font-medium transition-colors',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600',
              isActive
                ? 'border-brand-600 bg-brand-600 text-white'
                : 'border-line bg-surface text-ink hover:bg-slate-50',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Переключатель «включено/выключено». Это кнопка, а не чекбокс: нажимать по
 * нему будут в перчатках, и вся строка целиком — цель нажатия.
 */
export function Switch({
  isOn,
  label,
  hint,
  onChange,
}: {
  isOn: boolean;
  label: string;
  hint?: string;
  onChange: (isOn: boolean) => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={isOn}
      onClick={() => onChange(!isOn)}
      className={cn(
        'flex w-full items-center justify-between gap-4 rounded-xl border px-4 py-3 text-left transition-colors',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600',
        isOn ? 'border-brand-600 bg-brand-50' : 'border-line bg-surface hover:bg-slate-50',
      )}
    >
      <span className="min-w-0">
        <span className="block text-sm font-medium text-ink">{label}</span>
        {hint && <span className="mt-0.5 block text-sm text-muted">{hint}</span>}
      </span>

      {/* Дорожка с бегунком: цвет показывает состояние, положение дублирует его */}
      <span
        aria-hidden
        className={cn(
          'relative h-7 w-12 shrink-0 rounded-full transition-colors',
          isOn ? 'bg-brand-600' : 'bg-slate-300',
        )}
      >
        <span
          className={cn(
            'absolute top-1 size-5 rounded-full bg-white shadow transition-all',
            isOn ? 'left-6' : 'left-1',
          )}
        />
      </span>
    </button>
  );
}

// ------------------------------------------------------------------ карточки

export function Card({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <div className={cn('rounded-card border border-line bg-surface p-4 shadow-sm', className)}>
      {children}
    </div>
  );
}

export function SectionTitle({
  children,
  action,
}: {
  children: ReactNode;
  action?: ReactNode;
}): React.JSX.Element {
  return (
    <div className="mb-3 flex items-center justify-between gap-3">
      <h2 className="text-base font-semibold text-ink">{children}</h2>
      {action}
    </div>
  );
}

type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'brand';

const BADGE_TONES: Record<BadgeTone, string> = {
  neutral: 'bg-slate-100 text-slate-700',
  success: 'bg-emerald-50 text-emerald-700',
  warning: 'bg-amber-50 text-amber-700',
  danger: 'bg-red-50 text-red-700',
  brand: 'bg-brand-50 text-brand-700',
};

export function Badge({
  tone = 'neutral',
  children,
}: {
  tone?: BadgeTone;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        BADGE_TONES[tone],
      )}
    >
      {children}
    </span>
  );
}

/**
 * Инициалы в кружке. Цвет не зависит от человека: случайные оттенки из разных
 * цветовых семейств — ровно то, что палитра школы запрещает. Людей в списке
 * различают инициалы и имя рядом, а не хоровод фонов.
 *
 * `dark` — для тёмных поверхностей лендинга, где плотный асфальт сливается с фоном.
 */
export function Avatar({
  name,
  size = 'md',
  tone = 'solid',
}: {
  name: string;
  size?: 'sm' | 'md';
  tone?: 'solid' | 'dark';
}): React.JSX.Element {
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full font-semibold',
        size === 'sm' ? 'size-8 text-xs' : 'size-10 text-sm',
        tone === 'dark'
          ? 'border border-accent/40 bg-accent/10 text-accent'
          : 'bg-night text-white',
      )}
      aria-hidden
    >
      {initials}
    </span>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}): React.JSX.Element {
  return (
    <div className="rounded-card border border-dashed border-line bg-white/60 px-4 py-10 text-center">
      <p className="font-medium text-ink">{title}</p>
      {description && <p className="mx-auto mt-1 max-w-xs text-sm text-muted">{description}</p>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

export function ErrorNotice({ message }: { message: string }): React.JSX.Element {
  return (
    <div role="alert" className="rounded-xl border border-danger/20 bg-red-50 px-3 py-2 text-sm text-danger">
      {message}
    </div>
  );
}

export function SuccessNotice({ children }: { children: ReactNode }): React.JSX.Element {
  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
      {children}
    </div>
  );
}

// -------------------------------------------------------------- нижняя шторка

interface SheetProps {
  isOpen: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}

/**
 * Модальное окно, которое на телефоне выезжает снизу, а на широком экране
 * показывается по центру. Диалоги на телефоне удобнее держать под большим пальцем.
 */
export function Sheet({ isOpen, title, onClose, children, footer }: SheetProps): React.JSX.Element | null {
  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);

    // Фон не должен прокручиваться под открытой шторкой.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <button
        className="absolute inset-0 bg-slate-900/40"
        onClick={onClose}
        aria-label="Закрыть"
        type="button"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        /*
         * Цвет текста задан явно: шторку открывают и с тёмных страниц, где на
         * корне стоит text-white, — унаследовав его, заголовок и подписи стали
         * бы белым по белому.
         */
        className="relative flex max-h-[90dvh] w-full flex-col rounded-t-2xl bg-surface text-ink sm:max-w-lg sm:rounded-2xl"
      >
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <h2 className="text-base font-semibold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="-mr-2 flex size-9 items-center justify-center rounded-lg text-muted hover:bg-slate-100"
            aria-label="Закрыть"
          >
            ✕
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-4">{children}</div>
        {footer && <div className="safe-bottom border-t border-line px-4 pt-3">{footer}</div>}
      </div>
    </div>
  );
}

/**
 * Ссылка с кнопкой «Скопировать». Само поле только для чтения, но текст в нём
 * выделяется: если буфер обмена недоступен — а он требует защищённого
 * соединения, — ссылку всё равно можно взять руками.
 */
export function CopyableLink({ url }: { url: string }): React.JSX.Element {
  const [isCopied, setIsCopied] = useState(false);

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(url);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch {
      // Буфер недоступен — выделяем текст, дальше пользователь копирует сам.
      inputRef.current?.select();
    }
  };

  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="flex gap-2">
      <input
        ref={inputRef}
        readOnly
        value={url}
        onFocus={(event) => event.target.select()}
        className="min-h-11 w-full min-w-0 rounded-xl border border-line bg-surface px-3 text-xs text-ink"
      />
      <Button variant="secondary" onClick={() => void copy()} className="shrink-0">
        {isCopied ? 'Готово' : 'Копировать'}
      </Button>
    </div>
  );
}

export function PageLoader(): React.JSX.Element {
  return (
    <div className="flex justify-center py-16 text-muted">
      <Spinner className="size-7" />
    </div>
  );
}
